import {Button, Form} from "react-bootstrap"
import {useTranslation} from "react-i18next"
import {Eexam, ExamAid, ExamPeriod, ExamPlatform, ExamRun, ExamRunSchedule, ExamRunStatus, ExamRunType, ExamVariant, TenantOption} from "../types"

type Props = {
    value: Partial<ExamRun>
    eexams: Eexam[]
    periods: ExamPeriod[]
    aids: ExamAid[]
    tenants: TenantOption[]
    lang: "de" | "en"
    onChange: (value: Partial<ExamRun>) => void
}

const emptyVariant = (): ExamVariant => ({name: {de: "", en: ""}, durationMinutes: 60, moodleQuizId: 0})

const getId = (value: unknown): string => {
    if (!value) return ""
    if (typeof value === "string") return value
    if (typeof value === "object" && "_id" in value) return String((value as {_id: string})._id)
    return String(value)
}

const getIds = (values: unknown[] | undefined): string[] => (values ?? []).map(value => getId(value)).filter(Boolean)

export default function ExamRunForm({value, eexams, periods, aids, tenants, lang, onChange}: Props) {
    const {t} = useTranslation(["eexam"])
    const variants = value.variants && value.variants.length > 0 ? value.variants : [emptyVariant()]
    const selectedAidIds = getIds(value.aids as unknown[] | undefined)
    const selectedTenantIds = getIds(value.tenants as unknown[] | undefined)

    const updateVariant = (index: number, patch: Partial<ExamVariant>) => {
        const nextVariants = [...variants]
        nextVariants[index] = {...nextVariants[index], ...patch}
        onChange({...value, variants: nextVariants})
    }

    const removeVariant = (index: number) => {
        const nextVariants = variants.filter((_, currentIndex) => currentIndex !== index)
        onChange({...value, variants: nextVariants.length > 0 ? nextVariants : [emptyVariant()]})
    }

    const addVariant = () => onChange({...value, variants: [...variants, emptyVariant()]})

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.eexam")}</Form.Label>
                <Form.Select value={getId(value.eexam)} onChange={event => onChange({...value, eexam: event.target.value})}>
                    <option value="">{t("placeholders.select")}</option>
                    {eexams.map(eexam => <option key={eexam._id} value={eexam._id}>{eexam.title?.[lang]}</option>)}
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.schedule")}</Form.Label>
                <Form.Select value={value.schedule ?? "semester"} onChange={event => onChange({...value, schedule: event.target.value as ExamRunSchedule})}>
                    <option value="semester">{t("schedules.semester")}</option>
                    <option value="continuous">{t("schedules.continuous")}</option>
                </Form.Select>
            </Form.Group>
            {value.schedule !== "continuous" && (
                <Form.Group className="mb-3">
                    <Form.Label>{t("fields.period")}</Form.Label>
                    <Form.Select value={getId(value.period)} onChange={event => onChange({...value, period: event.target.value})}>
                        <option value="">{t("placeholders.select")}</option>
                        {periods.map(period => <option key={period._id} value={period._id}>{period.label}</option>)}
                    </Form.Select>
                </Form.Group>
            )}
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.type")}</Form.Label>
                <Form.Select value={value.type ?? "regular"} onChange={event => onChange({...value, type: event.target.value as ExamRunType})}>
                    <option value="regular">{t("runTypes.regular")}</option>
                    <option value="retake">{t("runTypes.retake")}</option>
                    <option value="bonus">{t("runTypes.bonus")}</option>
                    <option value="coursework">{t("runTypes.coursework")}</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.status")}</Form.Label>
                <Form.Select value={value.status ?? "draft"} onChange={event => onChange({...value, status: event.target.value as ExamRunStatus})}>
                    <option value="draft">{t("statuses.draft")}</option>
                    <option value="requested">{t("statuses.requested")}</option>
                    <option value="scheduled">{t("statuses.scheduled")}</option>
                    <option value="published">{t("statuses.published")}</option>
                    <option value="completed">{t("statuses.completed")}</option>
                    <option value="cancelled">{t("statuses.cancelled")}</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.entryOffsetMinutes")}</Form.Label>
                <Form.Control type="number" value={value.entryOffsetMinutes ?? 20} onChange={event => onChange({...value, entryOffsetMinutes: Number(event.target.value)})}/>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.tenants")}</Form.Label>
                <Form.Select multiple value={selectedTenantIds} onChange={event => onChange({...value, tenants: Array.from(event.target.selectedOptions).map(option => option.value)})}>
                    {tenants.map(tenant => <option key={tenant._id} value={tenant._id}>{tenant.describer} {tenant.name?.[lang] ? `– ${tenant.name[lang]}` : ""}</option>)}
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.platforms")}</Form.Label>
                <Form.Select multiple value={value.platforms ?? []} onChange={event => onChange({...value, platforms: Array.from(event.target.selectedOptions).map(option => option.value as ExamPlatform)})}>
                    <option value="computerLab">{t("platforms.computerLab")}</option>
                    <option value="personalDevice">{t("platforms.personalDevice")}</option>
                    <option value="smowl">{t("platforms.smowl")}</option>
                    <option value="examOnDemand">{t("platforms.examOnDemand")}</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>{t("fields.aids")}</Form.Label>
                <Form.Select multiple value={selectedAidIds} onChange={event => onChange({...value, aids: Array.from(event.target.selectedOptions).map(option => option.value)})}>
                    {aids.map(aid => <option key={aid._id} value={aid._id}>{aid.title?.[lang]}</option>)}
                </Form.Select>
            </Form.Group>
            <hr/>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">{t("variant.title")}</h5>
                <Button size="sm" variant="outline-primary" onClick={addVariant}><i className="bi bi-plus-lg me-1"/>{t("variant.add")}</Button>
            </div>
            {variants.map((variant, index) => (
                <div className="border rounded p-3 mb-3" key={index}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <strong>{t("variant.single", {number: index + 1})}</strong>
                        <Button size="sm" variant="outline-danger" onClick={() => removeVariant(index)} disabled={variants.length === 1}>{t("actions.delete")}</Button>
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("fields.titleDe")}</Form.Label>
                        <Form.Control value={variant.name?.de ?? ""} onChange={event => updateVariant(index, {name: {de: event.target.value, en: variant.name?.en ?? ""}})}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("fields.titleEn")}</Form.Label>
                        <Form.Control value={variant.name?.en ?? ""} onChange={event => updateVariant(index, {name: {de: variant.name?.de ?? "", en: event.target.value}})}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("fields.durationMinutes")}</Form.Label>
                        <Form.Control type="number" value={variant.durationMinutes ?? 60} onChange={event => updateVariant(index, {durationMinutes: Number(event.target.value)})}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("fields.moodleQuizId")}</Form.Label>
                        <Form.Control type="number" value={variant.moodleQuizId ?? 0} onChange={event => updateVariant(index, {moodleQuizId: Number(event.target.value)})}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("fields.tucanCode")}</Form.Label>
                        <Form.Control value={variant.tucanCode ?? ""} onChange={event => updateVariant(index, {tucanCode: event.target.value})}/>
                    </Form.Group>
                </div>
            ))}
        </>
    )
}
